import { createApi, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { filterDefined } from "../../../../helpers";
import { PaginationInfo } from "../../../../types/general-types";
import apiClient from "../../../../helpers/api-client";
import { determineAccountType } from "../../../../helpers/account";
import {
	RosterUser,
	RosterSortDirection,
	RosterStatistics,
	RosterSortKey,
	RosterAccount,
	BaseRosterAccount,
	PrivilegeGroup,
} from "../roster-types";

export type RosterUsersQueryArgs = {
	instituteUuid?: string;
	platform?: string[];
	name?: string;
	email?: string;
	lmsUserId?: string[];
	privilegesLocked?: boolean;
	privilegeGroupName?: string[];
	excludedPrivilegeGroupName?: string[];
	searchQuery?: string;
	sortBy?: RosterSortKey[];
	sortDirection?: RosterSortDirection[];
	page?: number;
	size?: number;
};

const SORT_BY_KEY_MAP: Record<RosterSortKey, string> = {
	email: "EMAIL",
	name: "NAME",
	privilegesLocked: "PRIVILEGES_LOCKED",
};

export const NON_STANDALONE_PLATFORMS = [
	"verity",
	"saml",
	"lms",
	"canvas",
	"moodle",
	"BlackboardLearn",
	"desire2learn",
];

function transformAccountResponse(account: BaseRosterAccount) {
	return { ...account, type: determineAccountType(account) } as RosterAccount;
}

export const rosterApi = createApi({
	reducerPath: "rosterApi",
	tagTypes: ["PrivilegeGroups", "RosterUser", "UserAccounts", "UserPrivilegeGroups", "RosterStatistics"],
	baseQuery: async (arg, api, extraOptions) =>
		fetchBaseQuery({
			fetchFn: apiClient.fetch.bind(apiClient),
			baseUrl: "/",
			prepareHeaders: (headers) => {
				headers.set("Content-Type", "application/json");
				return headers;
			},
		})(arg, api, extraOptions),
	endpoints: (builder) => ({
		getUsers: builder.query<
			{
				users: RosterUser[];
			} & PaginationInfo,
			RosterUsersQueryArgs
		>({
			query: ({
				instituteUuid,
				platform = [],
				name,
				email,
				lmsUserId = [],
				privilegesLocked,
				privilegeGroupName = [],
				excludedPrivilegeGroupName = [],
				searchQuery,
				sortBy = [],
				sortDirection = [],
				page,
				size,
			}) => {
				const searchParams = new URLSearchParams();
				if (instituteUuid) {
					searchParams.set("instituteId", instituteUuid);
				}
				platform.forEach((value) => searchParams.append("platform", value));
				if (name) {
					searchParams.set("name", name);
				}
				if (email) {
					searchParams.set("email", email);
				}
				lmsUserId.forEach((id) => searchParams.append("lmsUserId", id));
				if (privilegesLocked != null) {
					searchParams.set("privilegesLocked", String(privilegesLocked));
				}
				privilegeGroupName.forEach((pattern) => searchParams.append("privilegeGroupName", pattern));
				excludedPrivilegeGroupName.forEach((pattern) => searchParams.append("excludedPrivilegeGroupName", pattern));
				if (searchQuery) {
					searchParams.set("searchQuery", searchQuery);
					searchParams.append("searchField", "NAME");
					searchParams.append("searchField", "EMAIL");
					searchParams.append("searchField", "SECONDARY_LMS_USER_ID");
				}
				sortBy.forEach((field) => searchParams.append("sortBy", SORT_BY_KEY_MAP[field]));
				sortDirection.forEach((direction) => searchParams.append("sortDirection", String(direction).toUpperCase()));
				if (page != null) {
					searchParams.set("page", String(page));
				}
				if (size != null) {
					searchParams.set("size", String(size));
				}
				NON_STANDALONE_PLATFORMS.forEach((platform) => searchParams.append("platform", platform));
				return `api/access-control/user?${searchParams.toString()}`;
			},
			providesTags: (result) => [
				{
					type: "RosterUser",
				},
				...(result
					? result.users.map((user) => ({
							type: "RosterUser" as const,
							id: user.id,
						}))
					: []),
			],
		}),

		getUser: builder.query<RosterUser, string>({
			query: (userId) => `api/access-control/user/${userId}`,
			providesTags: (result) => (result ? [{ type: "RosterUser", id: result.id }] : ["RosterUser"]),
		}),

		getUserAccounts: builder.query<RosterAccount[], string>({
			query: (userId) => `api/access-control/account?userId=${userId}`,
			transformResponse: (
				result: {
					accounts: RosterAccount[];
				} & PaginationInfo,
			) => result.accounts.map(transformAccountResponse),
			providesTags: (_result, _error, userId) => [{ type: "UserAccounts", id: userId }],
		}),

		getPrivilegeGroups: builder.query<PrivilegeGroup[], { userId: string; privilegeGroupIds: number[] }>({
			queryFn: async ({ privilegeGroupIds }, _api, _extra, fetchWithBQ) => {
				if (privilegeGroupIds.length === 0) {
					return { data: [] };
				}
				const results = await Promise.all(
					privilegeGroupIds.map((id) => fetchWithBQ(`api/access-control/privilege/group/${id}`)),
				);
				if (results.every(({ error }) => error)) {
					return { error: results[0].error! };
				}
				return { data: filterDefined(results.map(({ data }) => data as PrivilegeGroup | undefined)) };
			},
			providesTags: (_result, _error, { userId }) => [{ type: "UserPrivilegeGroups", id: userId }],
		}),

		getRosterStatistics: builder.query<RosterStatistics, string>({
			query: (instituteUuid) => `api/access-control/roster/${instituteUuid}/stats`,
			providesTags: ["RosterStatistics"],
		}),

		createBasicAuthUser: builder.mutation<
			{ user: RosterUser; account: RosterAccount },
			{
				instituteUuid: string;
				email: string;
				firstName: string;
				lastName: string;
				privilegeGroupIds: number[];
			}
		>({
			queryFn: async ({ instituteUuid, email, firstName, lastName, privilegeGroupIds }, _api, _extra, fetchWithBQ) => {
				const accountResult = await fetchWithBQ({
					url: `api/access-control/basic-auth`,
					method: "POST",
					body: { email, sendEmail: false },
				});
				if (accountResult.error) {
					return { error: accountResult.error };
				}
				const createdAccount = accountResult.data as BaseRosterAccount;

				let fullName = firstName;
				if (firstName && lastName) {
					fullName += " ";
				}
				fullName += lastName;
				const accessControlUserResult = await fetchWithBQ({
					url: `api/access-control/user`,
					method: "POST",
					body: {
						instituteId: instituteUuid,
						name: fullName,
						privilegeGroupIds,
						timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
						platform: "verity",
					},
				});
				if (accessControlUserResult.error) {
					return { error: accessControlUserResult.error };
				}
				const createdAccessControlUser = accessControlUserResult.data as RosterUser;

				const [linkResult, verityUserResult] = await Promise.all([
					fetchWithBQ({
						url: `api/access-control/account/${createdAccount.id}/users/${createdAccessControlUser.id}`,
						method: "PUT",
					}),
					fetchWithBQ({
						url: `api/verity/institute-user`,
						method: "POST",
						body: {
							instituteUuid,
							firstName,
							lastName,
							accessControlUuid: createdAccessControlUser.id,
						},
					}),
				]);
				if (linkResult.error) {
					return { error: linkResult.error };
				}
				if (verityUserResult.error) {
					return { error: verityUserResult.error };
				}

				const passwordResetResult = await fetchWithBQ({
					url: `api/access-control/basic-auth/reset-password`,
					method: "POST",
					body: { email },
				});
				if (passwordResetResult.error) {
					return { error: passwordResetResult.error };
				}

				return {
					data: { user: createdAccessControlUser, account: transformAccountResponse(createdAccount) },
				};
			},
			invalidatesTags: ["RosterUser"],
		}),

		updateUser: builder.mutation<
			RosterUser,
			{
				id: string;
				privilegeGroupIds: number[];
			}
		>({
			queryFn: async (
				{ id, privilegeGroupIds },
				{ dispatch },
				_extra,
				fetchWithBQ,
			): Promise<
				| { error: any }
				| {
						data: RosterUser;
				  }
			> => {
				const userResult = await dispatch(rosterApi.endpoints.getUser.initiate(id, { forceRefetch: true }));
				if (userResult.error) {
					return { error: userResult.error };
				}
				const user = userResult.data as RosterUser;
				const existingPrivilegeGroupIdsSet = new Set(user.privilegeGroupIds);
				const newPrivilegeGroupIdsSet = new Set(privilegeGroupIds);
				const addedPrivilegeGroupIds = privilegeGroupIds.filter((id) => !existingPrivilegeGroupIdsSet.has(id));
				const removedPrivilegeGroupIds = user.privilegeGroupIds.filter((id) => !newPrivilegeGroupIdsSet.has(id));
				const privilegeGroupUpdateResults = await Promise.all(
					addedPrivilegeGroupIds
						.map((privilegeGroupId) =>
							fetchWithBQ({
								url: `api/access-control/user/${id}/privilege-group/${privilegeGroupId}`,
								method: "PUT",
							}),
						)
						.concat(
							removedPrivilegeGroupIds.map((privilegeGroupId) =>
								fetchWithBQ({
									url: `api/access-control/user/${id}/privilege-group/${privilegeGroupId}`,
									method: "DELETE",
								}),
							),
						),
				);
				const privilegeGroupUpdateErrorResult = privilegeGroupUpdateResults.find(
					(
						result,
					): result is {
						error: FetchBaseQueryError;
					} => !!result.error,
				);
				if (privilegeGroupUpdateErrorResult) {
					return privilegeGroupUpdateErrorResult;
				}

				return { data: { ...user, privilegeGroupIds } };
			},
			invalidatesTags: (_result, _error, { id }) => [
				{ type: "RosterUser" },
				{ type: "RosterUser", id },
				{ type: "UserPrivilegeGroups", id },
			],
		}),

		updateAccount: builder.mutation<
			RosterAccount,
			{ instituteUuid: string; userId: string; accountId: string } & (
				| {
						nrpsPrivilegeGroupMappingEnabled: boolean;
				  }
				| { samlPrivilegeGroupMappingEnabled: boolean }
			)
		>({
			query: ({ instituteUuid, accountId, ...args }) => {
				if ("nrpsPrivilegeGroupMappingEnabled" in args) {
					return {
						url: `api/access-control/lti1p3-account/${accountId}`,
						method: "PATCH",
						body: {
							instituteId: instituteUuid,
							nrpsPrivilegeGroupMappingEnabled: args.nrpsPrivilegeGroupMappingEnabled,
						},
					};
				} else if ("samlPrivilegeGroupMappingEnabled" in args) {
					return {
						url: `api/access-control/saml2-account/${accountId}`,
						method: "PATCH",
						body: {
							instituteId: instituteUuid,
							samlPrivilegeGroupMappingEnabled: args.samlPrivilegeGroupMappingEnabled,
						},
					};
				} else {
					return `api/access-control/account/${accountId}`;
				}
			},
			transformResponse: (result: BaseRosterAccount) => transformAccountResponse(result),
			invalidatesTags: (_result, _error, { userId }) => [{ type: "RosterUser" }, { type: "UserAccounts", id: userId }],
		}),
	}),
});

export const {
	useGetUsersQuery,
	useGetUserQuery,
	useGetUserAccountsQuery,
	useGetPrivilegeGroupsQuery,
	useGetRosterStatisticsQuery,
	useCreateBasicAuthUserMutation,
	useUpdateUserMutation,
	useUpdateAccountMutation,
} = rosterApi;
